// Translate stera's free-text `health_notes` deductions into friendly labels + tooltips.
//
// stera emits terse strings like "any-hand 25% (target 50%)" or "2 RGB frame gaps". We
// parse the number(s) out of each note and map by prefix to human copy + an (i) tooltip
// explaining the stera target / what it means. Unknown notes pass through verbatim so we
// never hide information.
//
// Each rule also recomputes the POINTS the note subtracted from the 0-100 score, mirroring
// the backend formulas (app/pipeline/stera_eval.py + stera's EvaluateConfig defaults for
// legacy notes). That lets list cards rank deductions by weight without a schema change.

export interface Deduction {
  label: string; // friendly human text (numbers substituted in)
  tip: string | null; // explanation of the stera target / meaning, for the (i) tooltip
  raw: string; // original note string (for title / debugging)
  points: number | null; // score points this note subtracted (null = not derivable)
}

/** First integer/decimal percentage in the string ("25%" -> 25). */
function firstPct(note: string): number | null {
  const m = note.match(/(\d+(?:\.\d+)?)\s*%/);
  return m ? Number(m[1]) : null;
}

/** First number anywhere in the string ("video 24.1 fps" -> 24.1). */
function firstNum(note: string): number | null {
  const m = note.match(/(\d+(?:\.\d+)?)/);
  return m ? Number(m[1]) : null;
}

/** Leading integer count ("2 RGB frame gaps" -> 2). */
function leadingCount(note: string): number | null {
  const m = note.match(/^\s*(\d+)/);
  return m ? Number(m[1]) : null;
}

const fmt = (n: number | null): string => (n == null ? "?" : String(n));

/** Backend deduction formula: max(0, target - actual) * weight. */
function deduct(target: number, actual: number | null, weight: number): number | null {
  if (actual == null) return null;
  return Math.max(0, target - actual) * weight;
}

type Rule = {
  test: (note: string) => boolean;
  build: (note: string) => Deduction;
};

const RULES: Rule[] = [
  {
    test: (n) => /^any-hand/i.test(n),
    build: (n) => ({
      raw: n,
      label: `Hands visible in ${fmt(firstPct(n))}% of sampled frames`,
      tip: "stera targets ≥50% any-hand presence; low = little manipulation content",
      points: deduct(50, firstPct(n), 0.15),
    }),
  },
  {
    test: (n) => /^≥?\s*1-hand/i.test(n) || /^>=?1-hand/i.test(n),
    build: (n) => ({
      raw: n,
      label: `≥1 hand in ${fmt(firstPct(n))}% of frames (legacy)`,
      tip: "old duplicate of the any-hand deduction (dropped after the denominator fix; % was diluted by the sampling stride)",
      points: deduct(30, firstPct(n), 0.05),
    }),
  },
  {
    // new-style note (correct sampled-frame denominator, left+right sides)
    test: (n) => /^both-hands/i.test(n),
    build: (n) => ({
      raw: n,
      label: `Both hands visible in ${fmt(firstPct(n))}% of sampled frames`,
      tip: "target ≥20% of sampled frames with both hands detected — bimanual manipulation",
      points: deduct(20, firstPct(n), 0.1),
    }),
  },
  {
    // legacy stera note (recordings processed before the denominator fix; % is diluted by the sampling stride)
    test: (n) => /^exactly-2-hand/i.test(n),
    build: (n) => ({
      raw: n,
      label: `Both hands visible in ${fmt(firstPct(n))}% of frames (legacy, understated)`,
      tip: "old deduction: % divided by ALL frames instead of sampled frames — real rate is ~stride× higher",
      points: deduct(20, firstPct(n), 0.1),
    }),
  },
  {
    test: (n) => /^video .*fps/i.test(n),
    build: (n) => ({
      raw: n,
      label: `Video at ${fmt(firstNum(n))} fps (buyers expect ~30)`,
      tip: "no penalty at ≥29 fps; below that, 2 pts per missing fps (capped at 15)",
      points: firstNum(n) == null ? null : Math.min(15, Math.max(0, (29 - (firstNum(n) as number)) * 2)),
    }),
  },
  {
    test: (n) => /^resolution/i.test(n),
    build: (n) => ({
      raw: n,
      label: `Resolution below 1080p (${n.replace(/^resolution\s*/i, "").replace(/\s*\(.*\)$/, "")})`,
      tip: "1080p on the shorter side is the baseline vendor floor — flat 15 pt deduction",
      points: 15,
    }),
  },
  {
    test: (n) => /^IMU .*Hz/i.test(n),
    build: (n) => ({
      raw: n,
      label: `IMU at ${fmt(firstNum(n))} Hz (buyers expect ≥100)`,
      tip: "no penalty at ≥95 Hz; below that, 0.2 pt per missing Hz (capped at 10)",
      points: firstNum(n) == null ? null : Math.min(10, Math.max(0, (95 - (firstNum(n) as number)) * 0.2)),
    }),
  },
  {
    test: (n) => /RGB frame gaps/i.test(n),
    build: (n) => ({
      raw: n,
      label: `${fmt(leadingCount(n))} dropped/late video frames`,
      tip: "2 pts per 1% of frames in gap (capped at 10); older recordings: 1 pt per gap capped at 10",
      // new format carries "(x% of frames)": proportional. Legacy: 1 pt per gap, cap 10.
      points: firstPct(n) != null
        ? Math.min(10, (firstPct(n) as number) * 2)
        : leadingCount(n) != null
          ? Math.min(10, leadingCount(n) as number)
          : null,
    }),
  },
  {
    test: (n) => /^no camera pose/i.test(n),
    build: (n) => ({
      raw: n,
      label: "No 6-DOF camera trajectory",
      tip: "Mode 1 has no pose by design (we disable this penalty)",
      points: 15,
    }),
  },
  {
    test: (n) => /^no imu/i.test(n),
    build: (n) => ({
      raw: n,
      label: "No IMU stream",
      tip: "recording carries no accelerometer/gyro data — flat 5 pt deduction",
      points: 5,
    }),
  },
  {
    test: (n) => /^no depth stream/i.test(n),
    build: (n) => ({
      raw: n,
      label: "No depth captured",
      tip: "this phone rig has no depth sensor (penalty disabled)",
      points: null,
    }),
  },
  {
    // "RGB↔Pose sync X% within 50ms" (also tolerate ASCII "RGB<->Pose")
    test: (n) => /pose sync/i.test(n) && /within/i.test(n),
    build: (n) => ({
      raw: n,
      label: `Only ${fmt(firstPct(n))}% of frames have a pose within 50 ms`,
      tip: "target ≥90%",
      points: deduct(90, firstPct(n), 0.1),
    }),
  },
  {
    test: (n) => /^depth valid/i.test(n),
    build: (n) => ({
      raw: n,
      label: `${fmt(firstPct(n))}% of depth pixels valid`,
      tip: "target ≥80%",
      points: deduct(80, firstPct(n), 0.3),
    }),
  },
];

/** Parse a single stera health_note into a friendly {label, tip, points}. */
export function parseDeduction(note: string): Deduction {
  const trimmed = note.trim();
  for (const rule of RULES) {
    if (rule.test(trimmed)) return rule.build(trimmed);
  }
  // Unknown deduction: show verbatim, no tooltip, unknown weight.
  return { label: trimmed, tip: null, raw: trimmed, points: null };
}

/** Parse a list of health_notes into friendly deductions. */
export function parseDeductions(notes: string[] | null | undefined): Deduction[] {
  return (notes ?? []).map(parseDeduction);
}

/** Deductions sorted heaviest-first (unknown weights last, original order kept). */
export function sortedDeductions(notes: string[] | null | undefined): Deduction[] {
  return parseDeductions(notes)
    .map((d, i) => ({ d, i }))
    .sort((a, b) => (b.d.points ?? -1) - (a.d.points ?? -1) || a.i - b.i)
    .map(({ d }) => d);
}
